import * as mobx from 'mobx';
import { action, observable } from 'mobx';
import * as IoC from 'typescript-ioc';
import {
    CustomPageConfigurationType,
    CustomPageType,
    ICustomPageConfig,
    ICustomPagesDAO,
    IEmbeddedHTMLCustomPageConfiguration,
    IEmbeddedHTMLCustomPageFileConfiguration,
    IMeetingPresenterGroupDTO
} from '../../../../../../../api/custom-pages.dao';
import { FormVmItemConfigType, FormVmValueType } from '../../../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { CustomPageFormField } from '../../../../../../../modules/passport-pages/custom-pages';
import {
    EMBEDDED_HTML_FILE_OBJECT_KEY,
    EMBEDDED_WEBPAGE_URL,
    EMBEDDED_ZIP_FILE,
    EMBED_CODE_ID,
    EXTERNAL_LINK_URL,
    LABEL,
    PATH,
    RICH_TEXT,
    MEETING_ID,
    RESTRICT_ACCESS_ENABLED,
    RESTRICT_ACCESS_FILTERS,
    PRESENTER_GROUPS_ENABLED,
    PRESENTER_GROUPS_FILTERS,
    PRESENTER_GROUPS,
} from '../../../../../../../modules/passport-pages/config';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';

export class CustomPageFormViewModel extends AbstractFormViewModelController {
    @mobx.observable
    public isFileUploading: boolean = false;
    @observable
    private isAccessFilters: boolean = true;
    @observable
    private isMeetingPresenterGroupsFilters: boolean = true;

    public static create(
        id: number | string,
        type: CustomPageType,
        props: ICustomPageConfig,
        config: FormVmItemConfigType[],
        accountId: number,
        eventId: number
    ): CustomPageFormViewModel {
        return new CustomPageFormViewModel(id, type, props, config, accountId, eventId);
    }

    public getId(): number | string {
        return this.id;
    }

    @action.bound
    public setFieldValueByKey(key: string, value: FormVmValueType): void {
        if (key === RESTRICT_ACCESS_ENABLED && value === false) {
            this.setValue(RESTRICT_ACCESS_FILTERS, []);
        }
        if (key === PRESENTER_GROUPS_ENABLED && value === false) {
            this.setValue(PRESENTER_GROUPS_FILTERS, []);
            this.setValue(PRESENTER_GROUPS, []);
        }
        this.setValue(key, value);
    }

    public getFieldByKey(key: string): CustomPageFormField {
        return {
            value: this.getValue(key),
            ...this.getValueValidationState(key)
        };
    }

    public getType(): CustomPageType {
        return this.type;
    }

    public getProps(): CustomPageConfigurationType {
        const props = this.getVmState().filter(el =>
            el.name !== EMBEDDED_ZIP_FILE && el.name !== EMBEDDED_HTML_FILE_OBJECT_KEY).reduce(
            (_props, { name, value }) => ({ ..._props, [name]: value }),
            { ...this.props } as CustomPageConfigurationType
        );
        return props;
    }

    public getHTMLFileProps(): IEmbeddedHTMLCustomPageFileConfiguration | null {
        const title = (this.getFieldByKey(EMBEDDED_ZIP_FILE).value as File).name;
        const objectKey = this.getFieldByKey(EMBEDDED_HTML_FILE_OBJECT_KEY).value as string;
        return title && objectKey ? {
            title: title,
            objectKey: objectKey
        } : null;
    }

    public getFieldConfigByKey(key: string): FormVmItemConfigType {
        return this.config.find(cnf => cnf.name === key);
    }

    @action.bound
    public changeType(type: CustomPageType): void {
        this.type = type;
    }

    public isPageValid(): boolean {
        return !this.checkAccessFilters() && !this.checkMeetingPresenterGroupsFilters() && this.getKeysToValidate()
            .every(key => this.getFieldByKey(key).valid)
        ;
    }

    private constructor(
        private readonly id: number | string,
        type: CustomPageType,
        private readonly props: ICustomPageConfig,
        private readonly config: FormVmItemConfigType[],
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly customPagesDAO: ICustomPagesDAO = IoC.Container.get(ICustomPagesDAO)
    ) {
        super(props, type, config);
        this.type = type;
        if (this.type === CustomPageType.EmbeddedHTML && (this.props as IEmbeddedHTMLCustomPageConfiguration).html) {
            this.setFieldValueByKey(EMBEDDED_ZIP_FILE, {name: (this.props as IEmbeddedHTMLCustomPageConfiguration).html.title} as File);
            this.setFieldValueByKey(EMBEDDED_HTML_FILE_OBJECT_KEY, (this.props as IEmbeddedHTMLCustomPageConfiguration).html.objectKey);
        }
    }

    @observable
    private type: CustomPageType;

    private getKeysToValidate(): string[] {
        const keys = [PATH, LABEL];

        if (this.type === CustomPageType.Knovio) return keys.concat(EMBED_CODE_ID);
        if (this.type === CustomPageType.EmbeddedWebpage) return keys.concat(EMBEDDED_WEBPAGE_URL);
        if (this.type === CustomPageType.RichText) return keys.concat(RICH_TEXT);
        if (this.type === CustomPageType.EmbeddedHTML) return keys.concat(EMBEDDED_ZIP_FILE);
        if (this.type === CustomPageType.UserShowcase) return keys.concat(EMBED_CODE_ID);
        if (this.type === CustomPageType.ExternalLink) return keys.concat(EXTERNAL_LINK_URL);
        if (this.type === CustomPageType.SingleMeeting) return keys.concat(MEETING_ID);
        if (this.type === CustomPageType.BookAMeeting) return keys; // Book a Meeting only needs PATH and LABEL
    }

    public setEmbeddedHTMLFile = (file: File) => {
        this.setFieldValueByKey(EMBEDDED_ZIP_FILE, file);
        this.setValueTouched(EMBEDDED_ZIP_FILE);
        if (!this.getFieldByKey(EMBEDDED_ZIP_FILE).valid || this.isFileUploading) return;
        this.uploadFileToS3(file);
    };

    @action
    private async uploadFileToS3(file: File) {
        try {
            this.isFileUploading = true;
            const response = await this.customPagesDAO.uploadFileToS3(file, {
                accountId: this.accountId,
                eventId: this.eventId
            });
            this.setFieldValueByKey(EMBEDDED_HTML_FILE_OBJECT_KEY, response.objectKey);
            this.setValueTouched(EMBEDDED_HTML_FILE_OBJECT_KEY);
        }
        catch (e) {
            alertServerError('Error', this.errorParsing.extractMessage(e));
        } finally {
            mobx.runInAction(() => {
                this.isFileUploading = false;
            });
        }
    }

    public get accessFilters(): boolean {
        return this.isAccessFilters;
    }

    @action.bound
    public setAccessFiltersValid(status: boolean){
        this.isAccessFilters = status;
    }

    @action.bound
    private checkAccessFilters(): boolean {
        const valid = this.getFieldByKey(RESTRICT_ACCESS_ENABLED).value === true &&
            !this.getFieldByKey(RESTRICT_ACCESS_FILTERS).value.toString().length;
        this.setAccessFiltersValid(!valid);
        return valid;
    }

    public get meetingPresenterGroupsFilters(): boolean {
        return this.isMeetingPresenterGroupsFilters;
    }

    @action.bound
    public setMeetingPresenterGroupsFiltersValid(status: boolean) {
        this.isMeetingPresenterGroupsFilters = status;
    }

    @action.bound
    private checkMeetingPresenterGroupsFilters(): boolean {
        const isEnabled = this.getFieldByKey(PRESENTER_GROUPS_ENABLED).value === true;
        const selectedIdsValue = this.getFieldByKey(PRESENTER_GROUPS_FILTERS).value;

        const selectedIds = Array.isArray(selectedIdsValue) ? selectedIdsValue : [];
        const hasSelectedIds = selectedIds.length > 0;

        const invalid = isEnabled && (!hasSelectedIds || !this.isMeetingPresenterGroupsFilters);

        this.setMeetingPresenterGroupsFiltersValid(!invalid);
        return invalid;
    }

    @action.bound
    public ensurePresenterGroupsSelected(): boolean {
        const enabled = Boolean(this.getFieldByKey(PRESENTER_GROUPS_ENABLED).value);
        const filtersValue = this.getFieldByKey(PRESENTER_GROUPS_FILTERS).value;
        const selectedIds = Array.isArray(filtersValue) ? filtersValue : [];
        if (!enabled || selectedIds.length > 0) return true;

        this.setMeetingPresenterGroupsFiltersValid(false);
        this.setValueTouched(PRESENTER_GROUPS_FILTERS);
        return false;
    }

    private getSelectedMeetingIds(ids: string[] = []): string[] {
        return Array.from(new Set(ids));
    }

    private uniqPresenterGroupsById(groups: IMeetingPresenterGroupDTO[]): IMeetingPresenterGroupDTO[] {
        const seen = new Set<number>();
        return groups.filter(group => {
            const key = Number(group.meetingId);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    @action.bound
    public initPresenterGroupsFilters(
        selectedMeetingIds: string[],
        presenterGroupsValue: IMeetingPresenterGroupDTO[]
    ): void {
        const presenterGroupIds = presenterGroupsValue.map(group => String(group.meetingId));
        const mergedIds = Array.from(new Set([...selectedMeetingIds, ...presenterGroupIds]));
        if (mergedIds.length === selectedMeetingIds.length &&
            mergedIds.every(id => selectedMeetingIds.includes(id))) {
            return;
        }
        this.setFieldValueByKey(PRESENTER_GROUPS_FILTERS, mergedIds);
    }

    @action.bound
    public addPresenterGroupsFilters(
        selectedIds: string[],
        meetingPresenterGroups: IMeetingPresenterGroupDTO[],
        meetingPresenterGroupsFiltersTouched: boolean
    ): void {
        if (!selectedIds?.length) return;
        const existingIds = this.getSelectedMeetingIds(this.getFieldByKey(PRESENTER_GROUPS_FILTERS).value as string[]);
        const mergedIds = Array.from(new Set([...existingIds, ...selectedIds.map(String)]));
        const basePresenterGroups = (this.getFieldByKey(PRESENTER_GROUPS).value as IMeetingPresenterGroupDTO[]) || [];
        const fromAllGroups = (meetingPresenterGroups || [])
            .filter(group => mergedIds.includes(String(group.meetingId)));
        const nextPresenterGroups = this.uniqPresenterGroupsById([...basePresenterGroups, ...fromAllGroups]);
        this.setFieldValueByKey(PRESENTER_GROUPS_FILTERS, mergedIds);
        this.setFieldValueByKey(PRESENTER_GROUPS, nextPresenterGroups);
        this.setMeetingPresenterGroupsFiltersValid(true);

        if (!meetingPresenterGroupsFiltersTouched) {
            this.setValueTouched(PRESENTER_GROUPS_ENABLED);
        }
    }

    @action.bound
    public removePresenterGroupByMeetingId(
        meetingId: string,
        presenterGroupsValue: IMeetingPresenterGroupDTO[]
    ): void {
        const nextPresenterGroups = presenterGroupsValue
            .filter(group => String(group.meetingId) !== meetingId);
        const nextIds = nextPresenterGroups.map(group => String(group.meetingId));
        this.setFieldValueByKey(PRESENTER_GROUPS_FILTERS, nextIds);
        this.setFieldValueByKey(PRESENTER_GROUPS, nextPresenterGroups);
    }

    @action.bound
    public removePresenterGroupsByMeetingIds(
        meetingIds: string[],
        presenterGroupsValue: IMeetingPresenterGroupDTO[]
    ): void {
        const meetingIdsSet = new Set(meetingIds.map(String));
        const nextPresenterGroups = presenterGroupsValue
            .filter(group => !meetingIdsSet.has(String(group.meetingId)));
        const nextIds = nextPresenterGroups.map(group => String(group.meetingId));
        this.setFieldValueByKey(PRESENTER_GROUPS_FILTERS, nextIds);
        this.setFieldValueByKey(PRESENTER_GROUPS, nextPresenterGroups);
    }
}
